import { DEFAULT_COLORS } from '../contexts/BackgroundContext';
import { v4 as uuidv4 } from 'uuid';

import { uploadToFirebase } from './images';

import {
  ITAppElkEventArgs,
  ITAppElkInviteeArgs,
  ITAppElkLocationArgs,
  TAppElkAttendeeRole,
  TAppElkAttendeeStatus,
  TAppElkBackgroundAnimation,
  TAppElkEvent,
  TAppElkEventAnswer,
  TAppElkEventDetails,
  TAppElkEventMetadata,
  TAppElkEventQuestion,
  TAppElkEventTheme,
  TAppElkInvitee,
  TAppElkInviteStatus,
  TAppElkLocation,
  TAppElkMessage, TAppElkPhotoUploadMode,
  TElkGetAlbumDetailsResponse
} from 'TProtocol/prototypes/events/messages';
import { TAppAddress } from 'TProtocol/common/models';
import { isPast } from './time';

declare const ELK_WEBAPP_URL: string;

export enum EventsFilterType {
  All = 'ALL',
  HostedByYou = 'HOSTED_BY_YOU',
  Upcoming = 'UPCOMING',
  Past = 'PAST'
}

export class IUEvent {
  constructor({
                isPreview,
                title,
                id,
                lastAutoSaveId,
                url,
                colors,
                animation,
                photoUrl,
                isHost,
                isCreate,
                isClone,
                isFromInspiration,
                IsFromQuickCreate,
                isPlaceholderUrl,
                openInvite,
                description,
                modificationId,
                messageModificationId,
                publicGuestList,
                cancelledTimestamp,
                messages,
                messagesArePersonal,
                hostedBy,
                hostEmail,
                message,
                timeZone,
                remindersEnabled,
                aiGeneratedPhotoUrls,
                questions,
                existingAlbumId,
                albumDetails,
                prompt,
                backgroundAnimationSearchQuery,
                backgroundAnimationUrl,
                videoBackgroundDisabled,
                attendees,
                subtitle,
                location,
                date,
                dateQuery,
                startTime,
                endTime,

                hostUploadMode,
                loading,
                playlistId

              }: Partial<IUEvent> = {}) {
    const uuid = uuidv4();
    let urlToPut: string | undefined = undefined;
    if (url) {
      urlToPut = url;
    } else if (id) {
      urlToPut = `https://${ELK_WEBAPP_URL}/event/${id}`;
    } else {
      urlToPut = `https://${ELK_WEBAPP_URL}/event/${uuid}`;
    }
    this.isCreate = isCreate ?? true;
    this.isPreview = isPreview ?? true;
    this.isClone = isClone ?? false;
    this.isFromInspiration = isFromInspiration ?? false;
    this.IsFromQuickCreate = IsFromQuickCreate ?? false;
    this.isPlaceholderUrl = isPlaceholderUrl ?? true;
    this.title = title ?? '';
    this.id = id ?? uuid;
    this.lastAutoSaveId = lastAutoSaveId;
    this.url = urlToPut;
    this.colors = colors ?? DEFAULT_COLORS;
    this.animation = animation ?? TAppElkBackgroundAnimation.LIGHT_SOURCE;
    this.photoUrl = photoUrl;
    this.aiGeneratedPhotoUrls = aiGeneratedPhotoUrls;
    this.isHost = isHost ?? false;
    this.openInvite = openInvite ?? true;
    this.publicGuestList = publicGuestList ?? true;
    this.remindersEnabled = remindersEnabled ?? true;
    this.description = description ?? '';
    this.modificationId = modificationId ?? Date.now().toString();
    this.messageModificationId = messageModificationId ?? Date.now().toString();
    this.cancelledTimestamp = cancelledTimestamp;
    this.messages = messages ?? null;
    this.messagesArePersonal = messagesArePersonal ?? false;
    this.hostedBy = hostedBy ?? '';
    this.hostEmail = hostEmail;
    this.timeZone = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.message = message ?? '';
    this.questions = questions ?? [];
    this.existingAlbumId = existingAlbumId;
    this.albumDetails = albumDetails;
    this.prompt = prompt;
    this.attendees = attendees ?? [];
    this.subtitle = subtitle ?? '';
    this.location = location ?? new TAppElkLocation({ displayName: '' });
    this.date = date;
    this.dateQuery = dateQuery;
    this.startTime = startTime ?? 0;
    this.endTime = endTime ?? 0;
    this.errors = {};
    this.backgroundAnimationSearchQuery = backgroundAnimationSearchQuery;
    this.backgroundAnimationUrl = backgroundAnimationUrl;
    this.videoBackgroundDisabled = videoBackgroundDisabled;
    this.hostUploadMode = hostUploadMode;
    this.loading = loading ?? false;
    this.playlistId = playlistId;
  }

  isCreate: boolean;
  isPreview: boolean;
  isClone: boolean;
  isFromInspiration: boolean;
  IsFromQuickCreate: boolean;
  isPlaceholderUrl: boolean;
  title: string;
  id: string;
  lastAutoSaveId?: string;
  url: string;
  colors: IUEventColors;
  animation: TAppElkBackgroundAnimation;
  photoUrl?: string;
  aiGeneratedPhotoUrls?: string[];
  isHost: boolean;
  openInvite: boolean;
  publicGuestList: boolean;
  description: string;
  modificationId: string;
  messageModificationId: string;
  cancelledTimestamp?: number;
  messages: TAppElkMessage[] | null;
  messagesArePersonal: boolean;
  hostedBy: string;
  hostEmail?: string;
  message: string;
  timeZone: string;
  remindersEnabled: boolean;
  questions: TAppElkEventQuestion[];
  existingAlbumId?: string;
  albumDetails?: TElkGetAlbumDetailsResponse;
  prompt?: string;
  backgroundAnimationSearchQuery?: string;
  backgroundAnimationUrl?: string;
  videoBackgroundDisabled?: boolean;

  attendees: TAppElkInvitee[];
  subtitle: string;
  location: TAppElkLocation;
  date?: Date;
  dateQuery?: string;
  startTime: number;
  endTime: number;
  errors: IUEventErrors;
  hostUploadMode: TAppElkPhotoUploadMode | undefined;
  loading: boolean;
  playlistId?: string;
}

export const copy = (event?: IUEvent): IUEvent | undefined => {
  if (event === undefined) {
    return undefined;
  }

  return new IUEvent({
    ...event,
    colors: {...event.colors},
    messages: event.messages?.map((message) => {return new TAppElkMessage(message);}) ?? null,
    questions: event.questions?.map((question) => { return new TAppElkEventQuestion(question);}) ?? null,
    albumDetails: event.albumDetails ? new TElkGetAlbumDetailsResponse(event.albumDetails) : undefined,
    attendees: event.attendees.map((attendee) => {return new TAppElkInvitee(attendee);}),
    location: new TAppElkLocation(event.location),
    errors: {...event.errors}
  });
}

export class IUEventErrors {
  invalidTitle?: boolean;
  invalidStartDate?: boolean;
  invalidDatePair?: boolean;
  invalidDuration?: boolean;
  missingImage?: boolean;
  invalidLocation?: boolean;
}

export interface IUEventColors {
  primary: string;
  secondary: string;
  highlightText: string;
  text: string;
  buttonTextColor: string;
}

const ONE_DAY = 24 * 60 * 60 * 1000;

export const cloneEvent = (event: IUEvent): IUEvent => {
  const newEventId = uuidv4();

  const newEvent = new IUEvent({
    ...event,
    id: newEventId,
    isCreate: true,
    isClone: true,
    url: `https://${ELK_WEBAPP_URL}/event/${newEventId}`,
    startTime: Date.now() + ONE_DAY,
    endTime: 0,
    modificationId: Date.now().toString(),
    messageModificationId: Date.now().toString(),
    cancelledTimestamp: undefined,
    messages: []
  });

  newEvent.attendees.forEach((attendee) => {
    attendee.inviteeId = uuidv4();
    if (attendee.role !== TAppElkAttendeeRole.ORGANIZER) {
      attendee.inviteStatus = TAppElkInviteStatus.PENDING;
      attendee.rsvpStatus = TAppElkAttendeeStatus.INVITED;
    }
    attendee.additionalGuestCount = 0;
  });

  return newEvent;
};

export const convertToTAppElkEvent = async (iuEvent: IUEvent, userName: string): Promise<TAppElkEvent> => {
  const title = iuEvent.title;
  const tzID: string = iuEvent.timeZone;
  let photoUrl: string | undefined = iuEvent.photoUrl;
  if (photoUrl?.match(/^(data|blob):/)) {
    photoUrl = await uploadToFirebase(photoUrl, iuEvent.id);
  }

  let hostString: string;
  if (iuEvent.hostedBy != '') {
    hostString = iuEvent.hostedBy;
  } else {
    hostString = userName;
  }

  return new TAppElkEvent({
    id: iuEvent.id,
    url: iuEvent.url,
    attendees: iuEvent.attendees.map(convertToTAppElkInvitee),
    organizerUuid: '',
    details: new TAppElkEventDetails({
      title: title,
      subtitle: iuEvent.subtitle,
      eventDescription: iuEvent.description,
      startTime: iuEvent.startTime,
      timezone: tzID,
      endTime: iuEvent.endTime,
      location: convertToTAppElkLocation(iuEvent.location),
      hostedBy: hostString,
      cancelledTimestamp: iuEvent.cancelledTimestamp,
      playlistId: iuEvent.playlistId
    }),
    theme: new TAppElkEventTheme({
      colors: [iuEvent.colors.primary, iuEvent.colors.secondary, iuEvent.colors.highlightText, iuEvent.colors.text, iuEvent.colors.buttonTextColor],
      animation: iuEvent.animation,
      backgroundAnimationSearchQuery: iuEvent.backgroundAnimationSearchQuery,
      backgroundAnimationUrl: iuEvent.backgroundAnimationUrl,
      videoBackgroundDisabled: iuEvent.videoBackgroundDisabled,
      photoUrl
    }),
    metadata: new TAppElkEventMetadata({
      openInvite: iuEvent.openInvite,
      publicGuestList: iuEvent.publicGuestList,
      disableReminders: !iuEvent.remindersEnabled
    }),
    questions: iuEvent.questions
  });
};

const convertToTAppElkInvitee = (invitee: ITAppElkInviteeArgs) => {
  return new TAppElkInvitee({
    ...invitee,
    answers: invitee.answers?.map(answer => new TAppElkEventAnswer(answer))
  });
};

const convertToTAppElkLocation = (location: ITAppElkLocationArgs) => {
  return new TAppElkLocation({
    displayName: location.displayName,
    address: new TAppAddress(location.address),
    placeName: location.placeName
  });
};

export const isHost = (event: IUEvent, userId: string) => event.attendees.find(
  attendee => attendee.role === TAppElkAttendeeRole.ORGANIZER && attendee.userId === userId
);

export type EventsByType = {
  [key in EventsFilterType]: IUEvent[];
};

// TODO: implement for toggling multiple event filters
// export const filterEvents = (events: IUEvent[], filterTypes: Set<EventsFilterType>): IUEvent[] => {
//   if (filterTypes.has(EventsFilterType.All)) {
//     return [...events];
//   }
//
//   const filteredEvents: TAppElkInvitee[] = [];
//
//   events.forEach(event => {
//
//   });
//
//   return filteredEvents;
// };

export const separateEvents = (events: IUEvent[], userId: string): EventsByType => {
  const map: EventsByType = {
    [EventsFilterType.All]: [],
    [EventsFilterType.Past]: [],
    [EventsFilterType.HostedByYou]: [],
    [EventsFilterType.Upcoming]: [],
  }

  events.forEach(event => {
    map[EventsFilterType.All].push(event);

    if (isPast(event)) {
      map[EventsFilterType.Past].push(event);
    } else {
      map[EventsFilterType.Upcoming].push(event);
    }

    if (isHost(event, userId)) {
      map[EventsFilterType.HostedByYou].push(event);
    }
  });

  return map;
};
